import EmblaCarousel, { type EmblaCarouselType } from 'embla-carousel'
import ClassNames from 'embla-carousel-class-names'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import { type UcoastVideo } from '@/scripts/core/ucoast-video'
import { fadeOverrides, toggleEmblaReady } from '@/scripts/theme/embla-base'

// Reasons autoplay is held. Autoplay runs only while the set is empty, so a
// hover ending can't override a visitor's explicit pause, and so on.
type PauseReason = 'user' | 'focus' | 'drag' | 'offscreen' | 'hidden' | 'editor'

/**
 * Hero carousel (sections/hero-carousel.liquid). Each slide is a full hero
 * rendered by snippets/hero-slide.liquid.
 *
 * Autoplay is our own timer rather than embla-carousel-autoplay: the plugin
 * resumes on mouseleave/pointerup/focusout regardless of a user pause, and its
 * interval doesn't restart when someone navigates manually.
 *
 * Media: slide 1 loads like a normal hero (LCP). Later slides render with
 * data-uc-load-on-event so the global MediaManager skips them; this component
 * loads the active slide, preloads its neighbours' images after page load, and
 * keeps videos in hidden slides paused.
 */
export class HeroCarousel extends UcoastEl {
	static htmlSelector = 'hero-carousel'
	emblaApi?: EmblaCarouselType
	slides: HTMLElement[] = []
	dots: HTMLButtonElement[] = []
	toggleButton: HTMLButtonElement | null = null
	autoplay = false
	delay = 6000
	timer = 0
	pauseReasons = new Set<PauseReason>(['offscreen'])
	inView = false
	pageLoaded = false
	observer?: IntersectionObserver
	abortController?: AbortController

	override connectedCallback() {
		super.connectedCallback()
		if (this.emblaApi || !this.hasAttribute('data-hc-active')) return
		this.init()
	}

	override disconnectedCallback() {
		super.disconnectedCallback()
		this.destroy()
	}

	init() {
		const viewport = this.querySelector<HTMLElement>(':scope > .embla')
		if (!viewport) return
		this.slides = Array.from(
			this.querySelectorAll<HTMLElement>('[data-hc-slide]')
		)
		this.dots = Array.from(
			this.querySelectorAll<HTMLButtonElement>('[data-hc-dot]')
		)
		this.toggleButton =
			this.querySelector<HTMLButtonElement>('[data-hc-toggle]')

		const reducedMotion = window.matchMedia(
			'(prefers-reduced-motion: reduce)'
		).matches
		this.autoplay = this.dataset.autoplay === 'true' && !reducedMotion
		this.delay = Number(this.dataset.autoplayDelay) || 6000
		if (this.toggleButton && !this.autoplay) this.toggleButton.hidden = true

		const api = EmblaCarousel(
			viewport,
			{
				loop: true,
				duration: 30,
				watchDrag: this.dataset.swipe === 'true',
			},
			[ClassNames({ inView: '' })]
		)
		this.emblaApi = api

		if (this.getAttribute('data-embla-fade') === 'all') {
			fadeOverrides(api)
			// reInit builds a new engine, which turns translation back on
			api.on('reInit', fadeOverrides)
		}
		api.on('init', toggleEmblaReady)
			.on('resize', toggleEmblaReady)
			.on('reInit', toggleEmblaReady)
			.on('reInit', () => this.updateInk())
			.on('select', () => this.onSelect())
			.on('pointerDown', () => this.pause('drag'))
			.on('pointerUp', () => this.resume('drag'))

		// Copy re-animates on each slide change (see section-hero-carousel.scss).
		// Added after ClassNames has marked slide 1, so slide 1 isn't restarted.
		this.classList.add('hero-carousel--started')

		this.abortController = new AbortController()
		this.bindControls(this.abortController.signal)
		this.bindPauseTriggers(this.abortController.signal)
		this.observeViewport()
		this.whenPageLoaded(() => {
			this.pageLoaded = true
			this.preloadNeighbours()
		})
		this.onSelect()
	}

	destroy() {
		window.clearTimeout(this.timer)
		this.abortController?.abort()
		this.observer?.disconnect()
		this.emblaApi?.destroy()
		this.emblaApi = undefined
	}

	get index() {
		return this.emblaApi?.selectedScrollSnap() ?? 0
	}

	onSelect() {
		const index = this.index
		this.slides.forEach((slide, i) => {
			const active = i === index
			slide.inert = !active
			this.setVideosOwned(slide, active)
		})
		this.dots.forEach((dot, i) => {
			const active = i === index
			dot.classList.toggle('is-active', active)
			if (active) dot.setAttribute('aria-current', 'true')
			else dot.removeAttribute('aria-current')
		})
		this.activateMedia()
		this.preloadNeighbours()
		this.updateInk()
		this.restartTimer()
	}

	// ── Controls ─────────────────────────────────────────────

	bindControls(signal: AbortSignal) {
		this.querySelector('[data-hc-prev]')?.addEventListener(
			'click',
			() => this.emblaApi?.scrollPrev(),
			{ signal }
		)
		this.querySelector('[data-hc-next]')?.addEventListener(
			'click',
			() => this.emblaApi?.scrollNext(),
			{ signal }
		)
		this.dots.forEach((dot, i) => {
			dot.addEventListener('click', () => this.emblaApi?.scrollTo(i), {
				signal,
			})
		})
		this.toggleButton?.addEventListener(
			'click',
			() => {
				if (this.pauseReasons.has('user')) this.resume('user')
				else this.pause('user')
				this.updateToggle()
			},
			{ signal }
		)
	}

	updateToggle() {
		if (!this.toggleButton) return
		const paused = this.pauseReasons.has('user')
		this.classList.toggle('is-user-paused', paused)
		const label = paused
			? this.toggleButton.dataset.labelPlay
			: this.toggleButton.dataset.labelPause
		if (label) this.toggleButton.setAttribute('aria-label', label)
	}

	// Controls sit over whatever slide is showing, so they take its text colour
	updateInk() {
		const inner = this.slides[this.index]?.querySelector(
			'.hero-statement__inner'
		)
		if (!inner) return
		this.style.setProperty('--hc-ink', getComputedStyle(inner).color)
	}

	// ── Autoplay ─────────────────────────────────────────────

	bindPauseTriggers(signal: AbortSignal) {
		// Keyboard focus only: a mouse click on an arrow shouldn't stop autoplay
		this.addEventListener(
			'focusin',
			(event) => {
				const target = event.target
				if (target instanceof HTMLElement && target.matches(':focus-visible'))
					this.pause('focus')
			},
			{ signal }
		)
		this.addEventListener(
			'focusout',
			(event) => {
				const next = event.relatedTarget
				if (next instanceof Node && this.contains(next)) return
				this.resume('focus')
			},
			{ signal }
		)
		document.addEventListener(
			'visibilitychange',
			() => {
				if (document.hidden) this.pause('hidden')
				else this.resume('hidden')
			},
			{ signal }
		)
		// Theme editor: selecting a slide block shows it and holds autoplay
		document.addEventListener(
			'shopify:block:select',
			(event) => {
				const i = this.slideIndexOf(event.target)
				if (i < 0) return
				this.pause('editor')
				this.emblaApi?.scrollTo(i, true)
			},
			{ signal }
		)
		document.addEventListener(
			'shopify:block:deselect',
			(event) => {
				if (this.slideIndexOf(event.target) < 0) return
				this.resume('editor')
			},
			{ signal }
		)
	}

	slideIndexOf(target: EventTarget | null) {
		if (!(target instanceof Node)) return -1
		return this.slides.findIndex(
			(slide) => slide === target || slide.contains(target)
		)
	}

	pause(reason: PauseReason) {
		this.pauseReasons.add(reason)
		this.restartTimer()
	}

	resume(reason: PauseReason) {
		if (!this.pauseReasons.delete(reason)) return
		this.restartTimer()
	}

	// Called on every slide change too, so manual navigation gets a full delay
	restartTimer() {
		window.clearTimeout(this.timer)
		const playing = this.autoplay && this.pauseReasons.size === 0
		this.classList.toggle('is-playing', playing)
		if (!playing) return
		this.timer = window.setTimeout(
			() => this.emblaApi?.scrollNext(),
			this.delay
		)
	}

	// ── Media ────────────────────────────────────────────────

	observeViewport() {
		this.observer = new IntersectionObserver(([entry]) => {
			this.inView = entry.isIntersecting
			if (this.inView) {
				this.activateMedia()
				this.resume('offscreen')
			} else {
				void window.Ucoast?.mediaManager?.pauseAllInContainer(this)
				this.pause('offscreen')
			}
		})
		this.observer.observe(this)
	}

	activateMedia() {
		if (!this.inView) return
		const slide = this.slides[this.index]
		if (!slide) return
		void window.Ucoast?.mediaManager?.loadAllInContainer(slide)
	}

	preloadNeighbours() {
		if (!this.pageLoaded) return
		const count = this.slides.length
		const index = this.index
		;[index + 1, index - 1].forEach((i) => {
			const slide = this.slides[(i + count) % count]
			if (slide) window.Ucoast?.mediaManager?.loadImagesInContainer(slide)
		})
	}

	// Hidden slides' videos are flagged so the global MediaManager's scroll
	// handler doesn't start them again behind the visible slide.
	setVideosOwned(slide: HTMLElement, active: boolean) {
		slide.querySelectorAll<UcoastVideo>('ucoast-video').forEach((video) => {
			if (active) {
				video.removeAttribute('data-uc-load-on-event')
			} else {
				video.setAttribute('data-uc-load-on-event', '')
				video.pause()
			}
		})
	}

	whenPageLoaded(callback: () => void) {
		if (document.readyState === 'complete') callback()
		else window.addEventListener('load', callback, { once: true })
	}
}
